import type { ArtKind } from "./artDraft";

/**
 * 🎲 **무엇을 그릴까 — 대신 골라 주기.**
 *
 * ## 왜 필요한가
 * 빈 칸이 가장 흔한 벽이다. 「무엇을 그릴까요?」 앞에서 운영자는 **무엇을 적어야 그럴듯하게
 * 나오는지**를 모른다. 예시를 한 줄 띄워 두긴 했지만, 예시는 하나뿐이라 그것만 따라 하게 된다.
 *
 * ## 왜 AI 로 안 뽑나
 * 주제를 AI 에게 물으면 한 번 더 돈이 들고 느려지는데, 얻는 게 없다. 그리고 더 중요한 건
 * **이 책의 세계 안에서** 골라야 한다는 것이다 — 경주·신라·그림책이라는 테두리를 모르는
 * 모델은 「용」이나 「로봇」을 내놓는다. 그래서 손으로 고른 목록을 둔다.
 *
 * 목록은 **씨앗**이지 정답이 아니다. 뽑아 놓고 운영자가 고치는 게 정상 흐름이다.
 */

/**
 * 종류마다의 주제 씨앗. 전부 **0~7세 그림책**과 **경주·신라**의 테두리 안이다.
 * 무섭거나 싸우는 것, 글자가 들어가는 것은 넣지 않는다.
 */
export const ART_IDEAS: Record<ArtKind, readonly string[]> = {
  hero: [
    "노란 우비를 입고 장화를 신은 아이",
    "파란 모자를 쓴 탐험가 아이",
    "분홍 한복 저고리를 입은 아이",
    "별무늬 잠옷을 입고 곰인형을 안은 아이",
    "초록 멜빵바지에 나뭇잎을 든 아이",
    "빨간 목도리를 두르고 손을 흔드는 아이",
    "흰 티셔츠에 병아리를 안은 아이",
    "밀짚모자를 쓰고 물통을 멘 아이",
  ],
  hair: [
    "밀짚모자, 챙이 넓고 노란 지푸라기",
    "신라 금관을 닮은 작고 동그란 금빛 관",
    "빨간 털실로 뜬 방울 달린 겨울 모자",
    "연둣빛 나뭇잎 두 장을 얹은 머리띠",
    "노란 병아리 모양 모자",
    "보라색 리본이 달린 밀짚 보닛",
    "하늘색 우비 모자",
    "작은 별이 세 개 달린 은빛 머리띠",
  ],
  acc: [
    "나무 지팡이, 끝에 작은 별이 달림",
    "황남빵이 담긴 작은 종이봉투",
    "손잡이가 둥근 노란 물통",
    "연꽃무늬가 그려진 작은 종이등",
    "초록 잎이 달린 작은 화분",
    "동그란 나무 방패연",
    "병아리 그림이 그려진 헝겊 가방",
    "작은 놋쇠 종",
  ],
  npc: [
    "아기 여우, 목도리를 두른 작은 여우",
    "안경을 쓴 검은 고양이 사서",
    "밀짚모자를 쓴 허수아비",
    "등을 든 작은 두꺼비",
    "빗자루를 든 다람쥐",
    "연잎 우산을 쓴 개구리",
    "떡을 파는 둥근 얼굴 아주머니",
    "달항아리를 안은 아기 곰",
  ],
  decor: [
    "작은 소나무, 굽은 줄기",
    "이끼 낀 둥근 바위",
    "한지로 만든 붉은 등",
    "나무로 만든 작은 이정표",
    "돌로 쌓은 낮은 돌탑",
    "노란 들꽃이 핀 작은 덤불",
    "기와가 얹힌 작은 우물",
    "대나무 세 그루",
  ],
  bg: [
    "달빛 아래 기와지붕 마을, 멀리 첨성대",
    "노을이 지는 능선과 둥근 무덤들",
    "연꽃이 뜬 넓은 연못과 정자",
    "벚꽃이 흩날리는 흙길",
    "눈이 소복이 쌓인 기와담 골목",
    "별이 가득한 밤하늘과 낮은 언덕",
    "초록 들판과 멀리 보이는 산",
    "등이 줄지어 걸린 저녁 시장 골목",
  ],
};

/**
 * 하나 뽑는다.
 *
 * @param rand  0~1 난수. 테스트에서 고정값을 넣으려고 받는다(`makeAdultQuiz` 와 같은 방식).
 * @param avoid 방금 뽑은 것. **같은 게 연달아 나오면 「고장 났나」로 보인다** —
 *              다시 눌렀는데 안 바뀌면 버튼이 안 먹은 것처럼 느껴진다.
 */
export function randomArtIdea(kind: ArtKind, rand: () => number = Math.random, avoid?: string): string {
  const all = ART_IDEAS[kind];
  const pool = avoid ? all.filter((x) => x !== avoid) : all;
  const list = pool.length > 0 ? pool : all; // 목록이 하나뿐이면 어쩔 수 없다
  const i = Math.min(list.length - 1, Math.max(0, Math.floor(rand() * list.length)));
  return list[i]!;
}
